
import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableColumn } from './SortableColumn';
import { Student, SortField, SortOrder } from '@/hooks/useStudentList';
import { Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

interface StudentTableProps {
  students: Student[];
  isLoading: boolean;
  sortField: SortField;
  sortOrder: SortOrder;
  onSort: (field: SortField) => void;
}

export const StudentTable: React.FC<StudentTableProps> = ({
  students,
  isLoading,
  sortField,
  sortOrder,
  onSort,
}) => {
  const handleCopyLink = (studentCode: string) => {
    const baseUrl = import.meta.env.VITE_PARENT_PORTAL_URL || '';
    const url = `${baseUrl}?fishsix_code=${studentCode}`;
    navigator.clipboard.writeText(url);
    toast.success('คัดลอกลิงก์สำเร็จ');
  };

  const renderSkeletonRows = () => {
    return Array(10)
      .fill(0)
      .map((_, index) => (
        <TableRow key={`skeleton-${index}`}>
          <TableCell><Skeleton className="h-4 w-16" /></TableCell>
          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
          <TableCell><Skeleton className="h-4 w-16" /></TableCell>
          <TableCell><Skeleton className="h-4 w-16" /></TableCell>
          <TableCell><Skeleton className="h-4 w-16" /></TableCell>
          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
          <TableCell><Skeleton className="h-4 w-8" /></TableCell>
        </TableRow>
      ));
  };

  return (
    <div className="rounded-md border overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableColumn 
              field="code" 
              label="รหัสนักเรียน"
              currentSortField={sortField}
              sortOrder={sortOrder}
              onSort={onSort}
            />
            <SortableColumn 
              field="first_name" 
              label="ชื่อ"
              currentSortField={sortField}
              sortOrder={sortOrder}
              onSort={onSort}
            />
            <SortableColumn 
              field="nickname" 
              label="ชื่อเล่น"
              currentSortField={sortField}
              sortOrder={sortOrder}
              onSort={onSort}
            />
            <SortableColumn 
              field="hours" 
              label="ชั่วโมงที่เหลือ"
              currentSortField={sortField}
              sortOrder={sortOrder}
              onSort={onSort}
            />
            <TableHead>ระดับการศึกษา</TableHead>
            <TableHead>โรงเรียน</TableHead>
            <TableHead>Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            renderSkeletonRows()
          ) : students.length > 0 ? (
            students.map((student) => (
              <TableRow key={student.code}>
                <TableCell className="font-medium">{student.code}</TableCell>
                <TableCell>{`${student.first_name} ${student.last_name || ''}`}</TableCell>
                <TableCell>{student.nickname || '-'}</TableCell>
                <TableCell className="text-right font-medium">{student.hours !== null ? student.hours : '-'}</TableCell>
                <TableCell>{student.education_level || '-'}</TableCell>
                <TableCell>{student.school || '-'}</TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleCopyLink(student.code)}
                    title="คัดลอกลิงก์ Parent Portal"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-4">
                ไม่พบข้อมูลนักเรียน
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};
